import React from "react";
import { Formik } from "formik";

import { CloneDishesInterface } from "../interfaces/interface";
import CloneDishModalForm from "./CloneDishModalForm";
import { CloneDishFormValues, useCloneDishModalBaseData } from "../hooks/useCloneDishModalBaseData";
import {
    useCloneMenuItemInMenuContentMutation,
    useManageMenuItemCategoryRelationMutation,
} from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess, MenuItemCloneInput } from "@me-team/host/main/graphql/types";
import { cloneNameToApi } from "../../Me-menu/utils/cloneNameUtils";
import { t } from "i18next";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

type CloneDishModalProps = {
    isOpen: boolean;
    onClose: () => void;
    menuId: number;
    branchId: number;
    onCloneSubmit: () => Promise<void> | void;
    cloneDishModalInfo: CloneDishesInterface;
    canCloneDish?: boolean;
    canEditMenuContent?: boolean;
};

const CloneDishModal: React.FC<CloneDishModalProps> = ({
    isOpen,
    onClose,
    menuId,
    branchId,
    onCloneSubmit,
    cloneDishModalInfo,
    canCloneDish,
    canEditMenuContent,
}) => {
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCloneDishByRole = useRoleAccess(CompanyAccess.MenuDishClone);
    const canEditMenuContentByRole = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canSubmitMenuContent = canEditMenuContent ?? canEditMenuContentByRole;
    const canSubmitClone = (canCloneDish ?? canCloneDishByRole) && canSubmitMenuContent;
    const {
        translate,
        loading,
        error,
        isTranslationMode,
        translationTooltip,
        dishNameVm,
        initialValues,
        validationSchema,
        categories,
        collapseSpaces,
        normalizeDishName,
        primaryLanguage,
        selectedLangVersionCode,
    } = useCloneDishModalBaseData({
        isOpen,
        menuId,
        cloneDishModalInfo,
    });

    const [cloneMenuItemInMenuContent] = useCloneMenuItemInMenuContentMutation({
        context: {
            errorType: "local",
        },
    });

    const [manageMenuItemCategoryRelation] = useManageMenuItemCategoryRelationMutation({
        context: {
            errorType: "local",
        },
    });

    return (
        <Formik<CloneDishFormValues>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
                if (!canSubmitClone) {
                    setSubmitting(false);
                    return;
                }

                const pickedCategoryId = values.currentSubcategoryId
                    ? values.currentSubcategoryId
                    : values.currentCategoryId;

                if (!cloneDishModalInfo?.cloneItem || !pickedCategoryId) {
                    setSubmitting(false);
                    return;
                }

                const companyId: number = Number(currentCompanyId);

                const normalizedName = cloneNameToApi(values?.dishName, t);

                const input: MenuItemCloneInput = {
                    name: normalizedName,
                    category: pickedCategoryId,
                };

                await cloneMenuItemInMenuContent({
                    variables: {
                        companyId: companyId,
                        branchId,
                        cloneMenuItemId: cloneDishModalInfo.cloneItem.id,
                        input,
                        locale: selectedLangVersionCode,
                    },
                    onCompleted: async (data) => {
                        await manageMenuItemCategoryRelation({
                            variables: {
                                companyId: companyId,
                                branchId,
                                menuId: menuId,
                                input: {
                                    menuItem: data.cloneMenuItemNew.id,
                                    category: pickedCategoryId,
                                },
                            },
                        });
                        await onCloneSubmit();
                        onClose();
                    },
                    onError: (error: ApolloError) => {
                        const validationErrors = error?.graphQLErrors[0]?.extensions?.category;
                        if (validationErrors === "menu_item.already_exists") {
                            setErrors({ dishName: t("A dish with this name already exists.") });
                        }
                        console.error("error", error);
                    },
                });

                setSubmitting(false);
            }}
        >
            {(formik) => (
                <CloneDishModalForm
                    primaryLanguage={primaryLanguage?.name}
                    isOpen={isOpen}
                    onClose={onClose}
                    formik={formik}
                    translate={translate}
                    loading={loading}
                    error={error}
                    isTranslationMode={isTranslationMode}
                    translationTooltip={translationTooltip}
                    dishNameVm={dishNameVm}
                    categories={categories}
                    collapseSpaces={collapseSpaces}
                    normalizeDishName={normalizeDishName}
                    canCloneDish={canSubmitClone}
                />
            )}
        </Formik>
    );
};

export default CloneDishModal;
